import { useState } from "react";
import { Loader2, MailWarning, X } from "lucide-react";

/**
 * Soft-nag verification banner (v3.98). Slim floating bar for logged-in
 * email/password users who haven't confirmed their address yet. Non-blocking:
 * they can dismiss it for the session. Resend re-triggers the confirmation email.
 * Rendered only when `show` is true (parent gates on user.needsEmailVerification).
 */
export function VerifyEmailBanner({ show, email }: { show: boolean; email?: string }) {
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!show || dismissed) return null;

  const resend = async () => {
    if (sending || sent) return;
    setSending(true);
    try {
      const res = await fetch("/api/auth/resend-verification", { method: "POST", credentials: "include" });
      if (res.ok) setSent(true);
    } catch {
      /* non-fatal — banner stays, they can retry */
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="verify-email-banner"
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-2rem)] max-w-lg"
    >
      <div className="flex items-center gap-3 rounded-xl px-4 py-3 shadow-lg" style={{ backgroundColor: "#1A1618", border: "1px solid #322a2c" }}>
        <MailWarning className="h-5 w-5 shrink-0" style={{ color: "#e0a97f" }} />
        <div className="flex-1 min-w-0 text-sm" style={{ color: "#f2eef2" }}>
          {sent ? (
            <span>Verification email sent{email ? ` to ${email}` : ""}. Check your inbox.</span>
          ) : (
            <span>Verify your email{email ? ` (${email})` : ""} to secure your account.</span>
          )}
        </div>
        {!sent && (
          <button
            onClick={resend}
            disabled={sending}
            data-testid="button-resend-verification"
            className="shrink-0 text-sm font-medium rounded-lg px-3 py-1.5 disabled:opacity-60"
            style={{ backgroundColor: "#8f2740", color: "#ffffff" }}
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resend"}
          </button>
        )}
        <button onClick={() => setDismissed(true)} aria-label="Dismiss" className="shrink-0 rounded-md p-1" style={{ color: "#9a9098" }}>
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
